import { useEffect, useMemo, useState } from 'react';
import { apiCall, formatApiErrorDetail } from '../utils/api';
import './opportunityRadar.css';

type Stage = 'prospect' | 'contacted' | 'follow_up' | 'proposal' | 'won' | 'lost';
type Opportunity = {
  id: string; name: string; company: string; source: string; stage: Stage;
  next_action: string; follow_up_date: string | null; pipeline_value: number;
  cash_collected: number; outstanding_payment: number; notes: string;
};

const emptyForm = {
  name: '', company: '', source: 'Direct', stage: 'prospect' as Stage,
  next_action: '', follow_up_date: '', pipeline_value: 0,
  cash_collected: 0, outstanding_payment: 0, notes: '',
};

const stageLabels: Record<Stage, string> = {
  prospect: 'Prospect', contacted: 'Contacted', follow_up: 'Follow-Up',
  proposal: 'Proposal', won: 'Won', lost: 'Lost',
};

const money = (value: number) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
}).format(value || 0);

export default function OpportunityRadar() {
  const [items, setItems] = useState<Opportunity[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const res = await apiCall('/api/opportunity-radar');
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      setItems(data.opportunities || []);
    } catch (e: any) { setError(e.message || 'Unable to load the pipeline.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const totals = useMemo(() => ({
    active: items.filter(i => !['won', 'lost'].includes(i.stage)).length,
    pipeline: items.filter(i => i.stage !== 'lost').reduce((s, i) => s + Number(i.pipeline_value || 0), 0),
    won: items.filter(i => i.stage === 'won').reduce((s, i) => s + Number(i.pipeline_value || 0), 0),
    collected: items.reduce((s, i) => s + Number(i.cash_collected || 0), 0),
    outstanding: items.reduce((s, i) => s + Number(i.outstanding_payment || 0), 0),
  }), [items]);

  const due = useMemo(() => items.filter(i => i.follow_up_date && i.stage !== 'won' && i.stage !== 'lost')
    .sort((a, b) => String(a.follow_up_date).localeCompare(String(b.follow_up_date))), [items]);

  const change = (key: string, value: string | number) => setForm(prev => ({ ...prev, [key]: value }));
  const reset = () => { setForm(emptyForm); setEditingId(null); setShowForm(false); setError(''); };

  const save = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const payload = { ...form, follow_up_date: form.follow_up_date || null };
      const res = await apiCall(`/api/opportunity-radar${editingId ? `/${editingId}` : ''}`, {
        method: editingId ? 'PUT' : 'POST', body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      reset(); await load();
    } catch (e: any) { setError(e.message || 'Unable to save.'); }
    finally { setSaving(false); }
  };

  const edit = (item: Opportunity) => {
    setForm({ ...emptyForm, ...item, follow_up_date: item.follow_up_date || '' });
    setEditingId(item.id); setShowForm(true); window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const remove = async (item: Opportunity) => {
    if (!window.confirm(`Remove ${item.name} from the pipeline?`)) return;
    const res = await apiCall(`/api/opportunity-radar/${item.id}`, { method: 'DELETE' });
    if (res.ok) load(); else setError('Unable to remove this opportunity.');
  };

  return <div className="radar">
    <div className="radar-title-row">
      <div><h1>NWG Opportunity Radar</h1><p>Independent deal flow • follow-ups • cash control</p></div>
      <button className="radar-primary" onClick={() => { reset(); setShowForm(true); }}>+ ADD PROSPECT</button>
    </div>

    {error && <div className="radar-error">{error}</div>}
    <div className="radar-metrics">
      <Metric label="Active Prospects" value={String(totals.active)} />
      <Metric label="Pipeline Value" value={money(totals.pipeline)} />
      <Metric label="Wins" value={money(totals.won)} />
      <Metric label="Cash Collected" value={money(totals.collected)} />
      <Metric label="Outstanding" value={money(totals.outstanding)} warning />
    </div>

    {showForm && <form className="radar-form" onSubmit={save}>
      <h2>{editingId ? 'Update Opportunity' : 'Add Prospect'}</h2>
      <div className="radar-form-grid">
        <Field label="Contact / Prospect"><input required value={form.name} onChange={e => change('name', e.target.value)} /></Field>
        <Field label="Company"><input value={form.company} onChange={e => change('company', e.target.value)} /></Field>
        <Field label="Source"><input value={form.source} onChange={e => change('source', e.target.value)} placeholder="LinkedIn, Skool, referral..." /></Field>
        <Field label="Stage"><select value={form.stage} onChange={e => change('stage', e.target.value)}>{Object.entries(stageLabels).map(([v,l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
        <Field label="Next Action"><input value={form.next_action} onChange={e => change('next_action', e.target.value)} placeholder="Call, send proposal, follow up..." /></Field>
        <Field label="Follow-Up Date"><input type="date" value={form.follow_up_date} onChange={e => change('follow_up_date', e.target.value)} /></Field>
        <Field label="Pipeline Value"><input type="number" min="0" value={form.pipeline_value} onChange={e => change('pipeline_value', Number(e.target.value))} /></Field>
        <Field label="Cash Collected"><input type="number" min="0" value={form.cash_collected} onChange={e => change('cash_collected', Number(e.target.value))} /></Field>
        <Field label="Outstanding Payment"><input type="number" min="0" value={form.outstanding_payment} onChange={e => change('outstanding_payment', Number(e.target.value))} /></Field>
        <Field label="Notes"><textarea value={form.notes} onChange={e => change('notes', e.target.value)} /></Field>
      </div>
      <div className="radar-actions"><button type="button" onClick={reset}>CANCEL</button><button className="radar-primary" disabled={saving}>{saving ? 'SAVING...' : 'SAVE OPPORTUNITY'}</button></div>
    </form>}

    <section className="radar-section"><h2>Next Follow-Ups</h2>
      {due.length === 0 ? <p className="radar-empty">No follow-ups scheduled.</p> : <div className="radar-followups">{due.slice(0, 5).map(i => <button key={i.id} onClick={() => edit(i)}><strong>{i.follow_up_date}</strong><span>{i.name}</span><small>{i.next_action || 'Set next action'}</small></button>)}</div>}
    </section>

    <section className="radar-section"><h2>Pipeline</h2>
      {loading ? <p className="radar-empty">Loading pipeline...</p> : items.length === 0 ? <p className="radar-empty">No prospects yet. Add the first real opportunity—no demo data.</p> :
      <div className="radar-table-wrap"><table><thead><tr><th>Prospect</th><th>Stage</th><th>Next Action</th><th>Follow-Up</th><th>Value</th><th>Collected</th><th></th></tr></thead><tbody>{items.map(i => <tr key={i.id}><td><strong>{i.name}</strong><small>{i.company || i.source}</small></td><td><span className={`radar-stage radar-${i.stage}`}>{stageLabels[i.stage]}</span></td><td>{i.next_action || '—'}</td><td>{i.follow_up_date || '—'}</td><td>{money(i.pipeline_value)}</td><td>{money(i.cash_collected)}</td><td><button onClick={() => edit(i)}>EDIT</button><button className="danger" onClick={() => remove(i)}>×</button></td></tr>)}</tbody></table></div>}
    </section>
  </div>;
}

function Metric({ label, value, warning=false }: { label:string; value:string; warning?:boolean }) {
  return <div className={warning ? 'radar-metric warning' : 'radar-metric'}><span>{label}</span><strong>{value}</strong></div>;
}
function Field({ label, children }: { label:string; children:React.ReactNode }) {
  return <label className="radar-field"><span>{label}</span>{children}</label>;
}
